import {useCallback, useEffect, useMemo, useState} from "react";
import {
  ProductExpansionScope,
  useShowProductsStore,
} from "store/product/show-products.store";
import {FullProduct} from "ts/api/product";
import {useSupplierCompanies} from "store/supplier-company/supplier-company.store";
import {useOrganiserCompany} from "store/organiser-company/organiser-company.store";
import {IGetOneOrganiserCompanyRes} from "@evennt-tech/api-v2-external";
import {
  useCategoryCodesState,
  useCategoryCodeStoreActions,
} from "store/categoryCode/category-code.store";
import useCurrentShow from "hooks/useCurrentShow";

type UseShowProductsByIdsResult = {
  products?: FullProduct[];
  isLoading?: boolean;
  isReady: boolean;
  // `true` once every requested id is accounted for — either present in the
  // expanded `products`, or confirmed absent from the show. Guards consumers
  // (e.g. the Cart) from acting on a still-resolving, partial set.
  isResolved: boolean;
  forgettableProductIds: Set<string>;
  fetchProducts: () => Promise<FullProduct[] | undefined>;
  organiserCompany?: IGetOneOrganiserCompanyRes | null;
};

const EMPTY_IDS: string[] = [];

const useShowProductsByIds = (
  showId: string,
  productIds: string[] = EMPTY_IDS
): UseShowProductsByIdsResult => {
  const {fetchManyCategoryCodes} = useCategoryCodeStoreActions();

  const [show, isLoadingShow] = useCurrentShow({showId});

  const {
    fetchProducts: fetchProductsAction,
    productsByIds,
    productsByIdsLoadedParams,
    productsByIdsLoading,
    expandedProductIds,
    getAllByShowAndProductIds,
    autoCatchProductsByIds,
    isLoading: isProductsByShowLoading,
  } = useShowProductsStore();

  // Keep the expanded result and readiness LOCAL to this hook instance so a
  // different page writing the shared `store.products` (e.g. ShoppingPage) can
  // never overwrite the Cart's product set during fast navigation.
  const [products, setProducts] = useState<FullProduct[]>([]);
  const [isReady, setIsReady] = useState(false);

  const idsKey = useMemo(() => [...productIds].sort().join(","), [productIds]);

  const hasLoadedRequestedIds =
    !idsKey || productsByIdsLoadedParams?.ids === idsKey;

  const showProducts = productsByIds;

  // A requested id is "resolved" when it is either present in the expanded
  // `products`, missing from the raw show fetch (genuinely removed), or it
  // already went through an expansion pass (present in raw but filtered out as
  // unavailable). Anything else is still loading — so consumers should wait.
  const isResolved = useMemo(() => {
    if (!productIds.length) {
      return true;
    }
    // The raw fetch must correspond to the currently requested ids; otherwise
    // it is stale (ids changed, refetch pending) and we are still resolving.
    if (!hasLoadedRequestedIds) {
      return false;
    }
    const expandedIds = new Set(products.map((product) => product.id));
    return productIds.every((id) => {
      if (expandedIds.has(id)) {
        return true;
      }
      const rawProduct = productsByIds.find((product) => product.id === id);
      if (!rawProduct) {
        return true;
      }
      return expandedProductIds.has(id);
    });
  }, [
    productIds,
    hasLoadedRequestedIds,
    products,
    productsByIds,
    expandedProductIds,
  ]);

  const forgettableProductIds = useMemo(() => {
    if (!hasLoadedRequestedIds) {
      return new Set<string>();
    }
    const rawIds = new Set(productsByIds.map((product) => product.id));
    const hiddenIds = new Set(
      products
        .filter((product) => product.isVisible === false)
        .map((product) => product.id)
    );

    return new Set(
      productIds.filter((id) => !rawIds.has(id) || hiddenIds.has(id))
    );
  }, [productIds, hasLoadedRequestedIds, productsByIds, products]);

  const {
    categoryCodesBySupplier,
    isCategoryCodesLoading,
  } = useCategoryCodesState();

  const {
    isLoading: organiserCompanyLoading,
    fetchOneByShowId: fetchOneOrganiserCompanyByShowId,
    selected: organiserCompanyToShow,
  } = useOrganiserCompany();

  const {
    isLoading: supplierCompaniesLoading,
    fetchByShow: fetchSupplierCompanyByShowId,
    itemsByShow: showSupplierCompanies,
  } = useSupplierCompanies();

  useEffect(() => {
    if (showId) {
      fetchSupplierCompanyByShowId({showId});
      fetchOneOrganiserCompanyByShowId({showId});
    }
  }, [showId]);

  useEffect(() => {
    if (showId && idsKey) {
      getAllByShowAndProductIds({showId, ids: idsKey});
      autoCatchProductsByIds();
    }
  }, [showId, idsKey]);

  const isLoading =
    isLoadingShow ||
    isProductsByShowLoading ||
    productsByIdsLoading ||
    !hasLoadedRequestedIds ||
    supplierCompaniesLoading ||
    organiserCompanyLoading ||
    isCategoryCodesLoading;

  const fetchProducts = useCallback(async () => {
    const result = await fetchProductsAction({
      showProducts,
      showSupplierCompanies,
      show,
      organiserCompanyToShow,
      isLoading,
      showId,
      scope: ProductExpansionScope.BY_IDS,
      categoryCodesBySupplier,
      fetchCategoryCodes: fetchManyCategoryCodes,
    });
    // Mirror the expansion result into local state (also used by direct
    // callers such as the Cart's surcharge/idle refresh). Early returns yield
    // `undefined` — keep the previous set until real data arrives.
    if (result) {
      setProducts(result);
    }
    return result;
  }, [
    fetchProductsAction,
    isLoading,
    organiserCompanyToShow,
    show,
    showId,
    showProducts,
    showSupplierCompanies,
    categoryCodesBySupplier,
    fetchManyCategoryCodes,
  ]);

  useEffect(() => {
    if (
      showProducts &&
      showSupplierCompanies &&
      show &&
      organiserCompanyToShow &&
      !isLoading
    ) {
      const getProducts = async (): Promise<void> => {
        setIsReady(false);
        try {
          await fetchProducts();
        } catch (e) {
          console.error("Error during loading show products by ids: ", e);
        } finally {
          setIsReady(true);
        }
      };

      getProducts();
    }
  }, [
    showProducts,
    showSupplierCompanies,
    show,
    organiserCompanyToShow,
    isLoading,
    fetchProducts,
  ]);

  return {
    products,
    isLoading: isLoading || !isReady,
    isReady,
    isResolved,
    forgettableProductIds,
    organiserCompany: organiserCompanyToShow,
    fetchProducts,
  };
};

export default useShowProductsByIds;
